import React, { useState } from "react";
import Dictionary from "../../../utils/Dictionary";
import dictionaryData from "../../../../constants/Data";

const Article = () => {
  const [hoveredWord, setHoveredWord] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseOver = (e) => {
    const word = e.target.innerText;
    const definition = dictionaryData[word];

    if (definition) {
      setHoveredWord({ word, definition });
      setPosition({ x: e.clientX, y: e.clientY });
    } else {
      setHoveredWord(null);
    }
  };

  const handleMouseOut = () => {
    setHoveredWord(null);
  };

  return (
    <div>
      {hoveredWord && (
        <Dictionary
          word={hoveredWord.word}
          definition={hoveredWord.definition}
          position={position}
        />
      )}
      <div>
        <h2 id="Checking-that-SpaC-is-expressed_s">
          Checking that SpaC is expressed{" "}
        </h2>
        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          In order to check that SpaC was <span>expressed</span> in our
          bacteria, we added a <span>Flag tag</span> to our protein and did a{" "}
          <span>Western blot</span> to detect SpaC via this tag. If our protein
          was expressed, we would be able to detect it thanks to{" "}
          <span>chemiluminescence</span>. You can refer to{" "}
          <a href="#Figure1">Figure 1</a> for the scheme of the working
          principle of Western blot.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure1"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/spac-western.png"
              alt="Experiment_SpaC"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 1:</u>{" "}
              </b>
              Schematic of the working system of Western blot on SpaC
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="Western_Blot_protocol">
              <mark>
                <b>Western Blot protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/western-blot-wiki.pdf"
              width="800"
              height="500"
              aria-label="Western blot"
            ></object>
          </details>
                 {" "}
        </center>
        <h2 id="theophylline_s">
          {" "}
          Checking that our theophylline-IPTG system works{" "}
        </h2>
        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          We designed an experiment to ensure our system functions correctly and
          that SpaC is not produced in the absence of <span>IPTG</span> and{" "}
          <span>theophylline</span>. In this experiment, we incubated our
          bacteria transformed with the SpaC construct with various conditions:
          just IPTG, just theophylline, both IPTG and theophylline, or neither.
          We also tested different induction protocols in order to know which
          would work best. For IPTG, one of our supervisors advised us to
          incubate our bacteria overnight at 18°C. For theophylline, the
          induction is usually done at 37°C for 2 hours
          <a href="#ref6">
            <sup>4</sup>{" "}
          </a>
          . Therefore, we decided to verify both of these conditions. After this
          incubation, we used a Western blot to check if SpaC was producedin
          each of the different conditions. Please refer to the Western blot
          protocol for further details.
        </p>

        <h2 id="Surface_s"> Checking that SpaC is on the surface </h2>
        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          To check that SpaC is on the surface of our bacteria, we used three
          different approaches : 1) a proteinase K assay, 2) the use of a GFP
          nanobody, which is fused to SpaC, and 3) immunofluorescence.
        </p>

        <h3 id="Proteinase-K-assay_s">Proteinase K assay</h3>
        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          We again used a proteinase K assay (see previous section) to determine
          if SpaC was on the surface as we did with EC20. The protocol for the
          proteinase K assay involves an incubation buffer containing sucrose.
          In the case of EC20, as it is <span>constitutively expressed</span>,
          the protein was generated but not yet surface-exposed at the time of
          proteinase K incubation. It remained undegraded and could be
          subsequently be detected by Western blot. To mitigate this unwanted
          signal, which diminishes the contrast between the negative control and
          the treated sample, we aim to induced a starvation-like state in the
          bacteria by omitting sucrose from the incubation buffer. Our
          hypothesis was that this adjustment would reduce the quantity of
          protein produced during the incubation. The working principle of this
          experiment is illustrated on <a href="#Figure2">Figure 2</a>.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure2"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/spac-protk.png"
              alt="Experiment_SpaC"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 2:</u>{" "}
              </b>
              Schematic of the working system of Proteinase K on SpaC
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="Proteinase_K_protocol">
              <mark>
                <b>Proteinase K protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/proteinase-k-assay.pdf"
              width="800"
              height="500"
              aria-label="Proteinase K assay"
            ></object>
          </details>
                 
        </center>

        <h3 className="mt-10 mb-5" id="GFP-nanobody-assay_s">
          GFP nanobody assay
        </h3>

        <p className="mb-20">
          In this experiment, we exposed our bacteria to green fluorescent
          protein (GFP). GFP exhibits a strong attraction to the nanobody that
          we engineered to be fused to SpaC. We based on our experimental work
          on Pierrat, Xavier, et al.
          <a href="#ref2">
            <sup>1</sup>{" "}
          </a>
          , which explains how the GFP nanobody system works. If the protein of
          interest is indeed located on the surface of the bacteria, following a
          series of washing steps, the GFP molecules will bind to the nanobody
          and will remain associated with the bacteria; if there is no surface
          expression, the GFP will be washed away. Therefore, we should observe
          a significantly higher level of GFP signal compared to a negative
          control if SpaC is expressed on the surface, providing further
          evidence of our conclusion that the protein is present on the
          bacterial surface. For this experiment, we used uninduced SpaC as a
          negative control. Unfortunately, obtaining a suitable positive control
          for this experiment proved challenging. Ideally, we would have used a
          strain solely expressing the nanobody on its surface; however due to
          time and cost limitations, we could not engineer this strain.. To
          create a gradient of expression levels, one bacterial culture was
          subjected to early induction, involving the presence of theophylline
          from the start of the secondary culture, resulting in high SpaC
          expression. In contrast to the bacterial culture that where
          theophylline was added together with IPTG for a duration of only 2
          hours, resulting in lower expression levels. A visual explanation can
          be seen bellow in <a href="#Figure3">Figure 3</a>.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure3"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/spac-gfp.png"
              alt="Experiment_SpaC"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 3:</u>{" "}
              </b>
              Schematic of the working system of the GFP nanobody assay
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="GFP_protocol">
              <mark>
                <b>GFP nanobody protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/gfp-nanobody-assay.pdf"
              width="800"
              height="500"
              aria-label="gfp nanobody assay"
            ></object>
          </details>
                 
        </center>

        <h3 className="mt-10 mb-5" id="Immunofluorescence_s">
          Immunofluorescence
        </h3>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          As with EC20, we conducted <span>immunofluorescence</span> experiments
          with our bacterial samples (please refer to the previous section). If
          we observed a significantly higher level of fluorescence in the
          bacteria expressing SpaC compared to the negative control, this would
          indicated the presence of SpaC on the surface of our bacteria. The
          schematic of this experiment is illustrated in{" "}
          <a href="#Figure4">Figure 4</a>.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure4"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/spac-if.png"
              alt="Experiment_SpaC"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 4:</u>{" "}
              </b>
              Schematic of the working system of Immunofluorescence on SpaC
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="IF_protocol">
              <mark>
                <b>Immunofluorescence protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/immunofluorescence-for-surface-expression-verification.pdf"
              width="800"
              height="500"
              aria-label="Immunofluorescence protocol"
            ></object>
          </details>
                 
        </center>

        <h2 className="mt-20" id="Half-life-of-SpaC_s">
          {" "}
          Half life of SpaC
        </h2>
        <hr className="dividergeneral mb-5"></hr>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Since SpaC serves as the mucus anchor for our bacteria, we conducted a
          series of Western blots to determine its <span>half life</span>. We
          performed Western lots at regular time points after having stopped the
          induction of SpaC. Initially, we assessed it every 6 hours, followed
          by evaluations every 12 hours, and ultimately, every 24 hours. The
          intensity of the signal obtained from the Western blots provided
          insight into the remaining protein levels.
        </p>

        <center>
          <details>
            <summary id="half_life_protocol">
              <mark>
                <b>Half life experiment protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/half-life.pdf"
              width="800"
              height="500"
              aria-label="half life experiment"
            ></object>
          </details>
                 
        </center>

        <h2 className="mt-20" id="mucus_s">
          {" "}
          Checking that Our bacteria is able to bind to mucus
        </h2>
        <hr className="dividergeneral mb-5"></hr>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Finally, we had to verify if our bacteria, in conjunction with SpaC,
          could effectively bind to <span>mucus</span>. For that purpose, we
          received the help of the Persat lab at EPFL, which works on microbial
          mechanics (many thanks to Sofya Mikhaleva for your help in the
          following experiments). Sofya referred us to the paper of Chin, Wai
          Hoe, et al.
          <a href="#ref4">
            <sup>2</sup>{" "}
          </a>
          , which describes “gut on a chip” assays. On our behalf, Sofya
          conducted the mucus binding experiment by culturing Caco2 cells on a{" "}
          <span>transwell</span> over several days and inducing mucus production
          in these cells. Once the mucus was ready, bacterial strains for
          testing and control were normalized to their{" "}
          <span>optical density</span> (OD), diluted to an appropriate
          concentration, and applied on top of the mucus. Then, the unbounded
          bacteria were washed away and the remaining bacteria are counted.
          Since the number of bacteria expressing SpaC was sufficiently higher
          than the negative control, we could deduce that SpaC effectively binds
          to mucus. The schematic of this experiment can be seen in{" "}
          <a href="#Figure5">Figure 5</a>.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure5"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/mucus.png"
              alt="Experiment_SpaC"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 5:</u>{" "}
              </b>
              Schematic of the working system of the mucus binding chip
              experiment
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="mucus_protocol">
              <mark>
                <b>Mucus binding experiment protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/mucus-binding-experiment.pdf"
              width="800"
              height="500"
              aria-label="mucus binding experiment"
            ></object>
          </details>
                 
        </center>

        <h2 className="mt-20" id="cloning_s">
          {" "}
          Cloning of SpaC construct
        </h2>
        <hr className="dividergeneral mb-5"></hr>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          As we realized that our initial SpaC construct was too large to be
          transported to our bacteria’s surfaces, we needed to reduce its size.
          We accomplished this by employing two distinct cloning techniques,
          Hifi and KLD. These methods enabled us to eliminate segments of the
          SpaC sequence that were not required for mucus binding. In their
          study, Kant, Abhiruchi, et al.
          <a href="#ref5">
            <sup>3</sup>{" "}
          </a>
          characterized the entire SpaC protein structure, identifying 3 stalk
          region domains, D3, D4, and D5, and the D1 and D2 domains as the
          binding region. Our primer design ensured the retention of only D2 in
          one instance and both D1 and D2 in another instance. The KLD method
          was suitable for maintaining the construct with both D1 and D2. But,
          given that the D2 domain sequence is sandwiched between two sections
          of the D1 sequence, another approach with several phases, culminating
          in Hifi cloning, was required. The schematic of the cloning of D1 and
          D2 thanks to KLD can be seen on <a href="#Figure6">Figure 6</a> and
          the cloning of D2 only thanks to Hifi can be seen on{" "}
          <a href="#Figure7">Figure 7</a>. On <a href="#Figure6">Figure 6</a>,
          the initial construct can be seen, containing SpaC, with all its
          domains (the stalk and the binding region), the fused GFP nanobody and
          Flag tag. After KLD cloning, we had removed the stalk region and
          everything else in the construct remained. On{" "}
          <a href="#Figure7">Figure 7</a>, the process was more complex. After a
          first <span>PCR</span>, we kept two fragments, the backbone without D1
          or the stalk region, and another fragment containing only the D2
          sequence. Then, we did a second PCR with this D2 fragment and homology
          arms are added to its sequence. Finally, the backbone and D2 fragments
          were bound together thanks to the Hifi method. After that, we again
          transformed our bacteria with those new <span>constructs</span>, and
          re-did the same experiments for the two shortened SpaC proteins as as
          we did for the full SpaC protein.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure6"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/cloning-d1d2.png"
              alt="Experiment_SpaC"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 6:</u>{" "}
              </b>
              Schematic of the KLD cloning of D1 and D2
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="KLD_protocol">
              <mark>
                <b>KLD cloning protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/kld-reaction.pdf"
              width="800"
              height="500"
              aria-label="KLD reaction"
            ></object>
          </details>
                 
        </center>

        <div class="centered mt-20">
          <figure>
            <img
              id="Figure7"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/cloning-d2.png"
              alt="Experiment_SpaC"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 7:</u>{" "}
              </b>
              Schematic of the Hifi cloning of D2
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="Hifi_protocol">
              <mark>
                <b>Hifi cloning protocol</b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/hifi-cloning.pdf"
              width="800"
              height="500"
              aria-label="hifi cloning"
            ></object>
          </details>
                 
        </center>

        <h2 className="mt-20" id="viability-assay_s">
          Evaluation of Plating Conditions for D2 and D1+D2 Domains in Mucus
          Binding Assays
        </h2>
        <hr className="dividergeneral mb-5"></hr>

        <p onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
          Plating induced bacteria producing D2 and D1+D2 domains on{" "}
          <span>agar plates</span> revealed distinct growth behaviour compared
          to BL21 cells (SpaC uninduced), our control, despite having similar OD
          values. However for our experiment to be consistent, we needed to have
          a control as similar as possible from our bacteria to be tested.
          Induced bacteria indeed displayed a significantly lower{" "}
          <span>colony</span> count, while the existing colonies exhibited a
          faster growth. To be able to implement the mucus binding experiment on
          our clones, we conducted experiments testing various conditions for
          plating the bacteria. We compared the three bacterial strains at our
          disposal, D2, D1+D2, and SpaC, under different induction conditions:
          IPTG, theophylline, both, or no induction at all. Our focus was on
          assessing the number of colonies and the size of the apparent
          colonies, in order to have bacterial colonies that look similar to the
          negative control. The schematic of the plating can be seen inn{" "}
          <a href="#Figure8">Figure 8</a>.
        </p>

        <div class="centered">
          <figure>
            <img
              id="Figure8"
              src="https://static.igem.wiki/teams/4888/wiki/experiments/agar-plate.png"
              alt="Experiment_conditions_plating"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 8:</u>{" "}
              </b>
              Schematic of the plating condition experiment
            </figcaption>
          </figure>
        </div>
        <br></br>

        <center>
          <details>
            <summary id="Test_protocol">
              <mark>
                <b>
                  Evaluation of Plating Conditions for D2 and D1+D2 Domains in
                  Mucus Binding Assays
                </b>
              </mark>
            </summary>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/experiments/protocoles/viability-assay-of-d1-and-d1.pdf"
              width="800"
              height="500"
              aria-label="viability assay"
            ></object>
          </details>
                 
        </center>

        <h2 className="mt-20" id="references_s">
          {" "}
          References{" "}
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <ul>
          <ol type="1">
            <li id="ref2">
              {" "}
              Pierrat, Xavier, et al. <br />{" "}
              <a href="https://journals.asm.org/doi/full/10.1128/mbio.01392-21">
                The mammalian membrane microenvironment regulates the sequential
                attachment of bacteria to host cells.
              </a>{" "}
              <br />
              Mbio 12.4 (2021): 10-1128{" "}
            </li>

            <li id="ref4">
              {" "}
              Chin, Wai Hoe, et al. <br />{" "}
              <a href="https://www.pnas.org/doi/abs/10.1073/pnas.2116197119">
                Bacteriophages evolve enhanced persistence to a mucosal surface.
              </a>{" "}
              <br />
              Proceedings of the National Academy of Sciences 119.27 (2022):
              e2116197119{" "}
            </li>

            <li id="ref5">
              {" "}
              Kant, Abhiruchi, et al. <br />{" "}
              <a href="https://www.sciencedirect.com/science/article/pii/S1047847720301441?casa_token=Eyh_GLSQVRsAAAAA:-L3vPvj9tdwotMZOotPXI2ELIga5O9neWi4PqH9Sdnpi82gYjAG_vzKbu5_UgUQDKQhL6rxtjn4">
                Crystal structure of lactobacillar SpaC reveals an atypical
                five-domain pilus tip adhesin: Exposing its substrate-binding
                and assembly in SpaCBA pili.
              </a>{" "}
              <br />
              Structural Biology 211.3 (2020): 107571{" "}
            </li>

            <li id="ref6">
              {" "}
              Topp, Shana, et al. <br />{" "}
              <a href="https://journals.asm.org/doi/full/10.1128/aem.01537-10?casa_token=piJY83sh5UUAAAAA%3AqIsXChdtYx8ZkXN8IHxaXTDTqkJ8ddyCu-2x56jDKPQ8bIZUrDYj6C-YAC97JFYynBVrNPOE7efeIQ">
                Synthetic riboswitches that induce gene expression in diverse
                bacterial species.
              </a>{" "}
              <br />
              Applied and environmental microbiology 76.23 (2010): 7881-7884{" "}
            </li>
          </ol>
        </ul>
        <br></br>
      </div>
    </div>
  );
};

export default Article;
